import { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "bootstrap/dist/css/bootstrap.css";
import "bootstrap/dist/js/bootstrap.js";
import AppContextProvider from "common/Context/AppContextProvider";
import UserContextProvider from "common/Context/UserContextProvider";
import Loading from "common/Components/Loading";
import "common/Auth/amplifyConfig";
import {
  handleAuthSuccess,
  handleAuthError,
  handleOAuthCallback,
  checkExistingAuth,
  clearLocalAuthSession,
  redirectToLogin,
  isUserUnAuthenticatedException
} from "common/Auth/cognitoAuth";
import {
  isPublicSignupPath,
  resumeSignupInviteAfterSignOut,
} from "common/Auth/publicAuthPaths";

const root = ReactDOM.createRoot(document.getElementById("root"));

const Root = () => {
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [publicSignupMode, setPublicSignupMode] = useState(false);

  useEffect(() => {
    const authenticate = async () => {
      try {
        if (resumeSignupInviteAfterSignOut()) {
          return;
        }

        if (isPublicSignupPath()) {
          await clearLocalAuthSession().catch(() => {});
          setPublicSignupMode(true);
          setIsAuthenticating(false);
          return;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const hasCode = urlParams.has('code');
        const hasError = urlParams.has('error');
        
        if (hasError) {
          const errorParam = urlParams.get('error');
          const errorDescription = urlParams.get('error_description');
          handleAuthError(
            { message: errorDescription || errorParam || 'Unknown error' },
            setAuthError,
            setIsAuthenticating
          );
          return;
        }
        
        if (hasCode) {
          await handleOAuthCallback(setUserProfile, setIsAuthenticating, setAuthError);
          return;
        }
        
        const profile = await checkExistingAuth();
        if (profile) {
          console.log("User already authenticated with Amplify");
          handleAuthSuccess(profile, setUserProfile, setIsAuthenticating);
          return;
        }
        
        await redirectToLogin(setAuthError, setIsAuthenticating);
        
      } catch (error) {
        console.error("Authentication error:", error);
        if (isUserUnAuthenticatedException(error)) {
          console.log("User not authenticated yet - will redirect to login");
          await redirectToLogin(setAuthError, setIsAuthenticating);
        } else {
          handleAuthError(error, setAuthError, setIsAuthenticating);
        }
      }
    };

    authenticate();
  }, []);

  if (isAuthenticating) {
    return (
      <div>
        <Loading loadingText="Authenticating...." />
      </div>
    );
  }

  if (authError) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h3>Authentication Error</h3>
        <p>{authError}</p>
        <button
          onClick={async () => {
            setAuthError(null);
            setIsAuthenticating(true);
            await redirectToLogin(setAuthError, setIsAuthenticating);
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <UserContextProvider>
      <AppContextProvider>
        <App profile={userProfile} publicSignupMode={publicSignupMode} />
      </AppContextProvider>
    </UserContextProvider>
  );
};
root.render(<Root />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
